import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { MediaPreviewPanel, type MediaPreviewPanelHandle, Sheet, SheetContent } from "@nexus/ui"
import type { TopicExampleRow } from "@nexus/contracts"
import { useAuth } from "@/features/auth/hooks/useAuth"
import { useDashboardFiltersStore } from "@/features/dashboard/stores/dashboardFilters.store"
import type { MyBrandCreativeCardModel } from "../../lib/drilldown-card.types"
import {
  drilldownWindowSpanDays,
  type DrilldownPageFiltersInput,
} from "@/lib/queries/drilldown.queries"
import type { MyBrandFiltersInput } from "@/lib/queries/dashboard.queries"
import { mapMyBrandDrawerModel } from "../../lib/my-brand-drawer-model"
import { mapMyBrandDrawerNarrative } from "../../lib/my-brand-drawer-narrative"
import { parseMyBrandDrawerRows } from "../../lib/parse-my-brand-drawer-data"
import {
  DRILLDOWN_DRAWER_CLOSE_CLASS,
  shouldKeepDrilldownDrawerOpenOnOutsideInteraction,
} from "../../lib/drilldown-drawer-utils"
import { useMyBrandDrawerDataSection } from "../../hooks/useMyBrandDrawerData"
import { useMyBrandDrawerPerformanceDataSection } from "../../hooks/useMyBrandDrawerPerformanceData"
import { useMyBrandDrawerFatigueDataSection } from "../../hooks/useMyBrandDrawerFatigueData"
import { useMyBrandDrawerWhyDataSection } from "../../hooks/useMyBrandDrawerWhyData"
import type { FatigueGranularity } from "../../lib/my-brand-drawer-fatigue-catalog"
import type { PerformanceKpiId } from "../../lib/my-brand-drawer-performance-catalog"
import { DrilldownMyBrandCreativeInfoTab } from "./DrilldownMyBrandCreativeInfoTab"
import { DrilldownMyBrandCreativeAdIdsTab } from "./DrilldownMyBrandCreativeAdIdsTab"
import { DrilldownMyBrandCreativePerformanceTab } from "./DrilldownMyBrandCreativePerformanceTab"
import { DrilldownMyBrandCreativeFatigueTab } from "./DrilldownMyBrandCreativeFatigueTab"
import { DrilldownMyBrandCreativeWhyTab } from "./DrilldownMyBrandCreativeWhyTab"
import { exampleName, exampleToCardModel, isWhyExampleStub } from "../../lib/my-brand-drawer-why"
import { DrilldownCreativeLinkMenu } from "./DrilldownCreativeLinkMenu"
import { useTenantCurrency } from "@/lib/use-tenant-currency"

type DrawerTab = "info" | "adIds" | "performance" | "why" | "fatigue"

export interface DrilldownMyBrandCreativeDrawerProps {
  creative: MyBrandCreativeCardModel | null
  open: boolean
  onOpenChange: (open: boolean) => void
  myBrandFilters: MyBrandFiltersInput
  drilldownFilters: DrilldownPageFiltersInput
  /** Swaps the drawer to another creative (the page's selected creative). */
  onSelectCreative?: (creative: MyBrandCreativeCardModel) => void
}

export function DrilldownMyBrandCreativeDrawer({
  creative,
  open,
  onOpenChange,
  myBrandFilters,
  drilldownFilters,
  onSelectCreative,
}: DrilldownMyBrandCreativeDrawerProps) {
  const { t } = useTranslation()
  const { symbol: currencySymbol } = useTenantCurrency()
  const activeTenantId = useAuth((s) => s.activeTenantId)
  const northStar = useDashboardFiltersStore((s) => s.northStar)
  const [activeTab, setActiveTab] = useState<DrawerTab>("info")
  const [fatigueSelectedMetrics, setFatigueSelectedMetrics] = useState<PerformanceKpiId[]>(["spend"])
  const [fatigueGranularity, setFatigueGranularity] = useState<FatigueGranularity>("weekly")
  const [showPreviousPeriod, setShowPreviousPeriod] = useState(true)
  const mediaPreviewRef = useRef<MediaPreviewPanelHandle>(null)
  const tabScrollRef = useRef<HTMLDivElement>(null)
  // "Why it works" example jumps: one level back, shown while the drawer still shows the
  // creative it jumped to. Names of creatives opened from examples label the way back.
  // Keyed by hash, not card id: the page may swap the example stub for the grid's own card.
  const [whyBack, setWhyBack] = useState<{
    to: MyBrandCreativeCardModel
    forHash: string
    label: string
  } | null>(null)
  const exampleNamesRef = useRef(new Map<string, string>())

  const creativeKey = `${creative?.hash ?? ""}|${creative?.adId ?? ""}`
  useEffect(() => {
    setFatigueSelectedMetrics(["spend"])
    setFatigueGranularity("weekly")
    setShowPreviousPeriod(true)
  }, [creativeKey])

  const drawerData = useMyBrandDrawerDataSection(creative, open, myBrandFilters, drilldownFilters)
  const performanceData = useMyBrandDrawerPerformanceDataSection(
    creative,
    open,
    activeTab,
    myBrandFilters,
    drilldownFilters,
  )
  const fatigueData = useMyBrandDrawerFatigueDataSection(
    creative,
    open,
    activeTab,
    myBrandFilters,
    drilldownFilters,
    fatigueSelectedMetrics,
    fatigueGranularity,
  )

  const whyData = useMyBrandDrawerWhyDataSection(
    creative,
    open,
    activeTab,
    myBrandFilters,
    drilldownFilters,
  )

  const fatigueSpanDays = useMemo(
    () => drilldownWindowSpanDays(drilldownFilters, myBrandFilters.dateRange),
    [drilldownFilters, myBrandFilters.dateRange],
  )

  const performanceRow = useMemo(() => {
    return performanceData.section?.rows[0] ?? null
  }, [performanceData.section?.rows])

  const tenants = useAuth((s) => s.tenants)
  const activeTenant = useMemo(
    () => tenants.find((tenant) => tenant.id === activeTenantId) ?? null,
    [tenants, activeTenantId],
  )

  const tenantLogoUrl = activeTenant?.logoUrl ?? null
  const tenantName = activeTenant?.name ?? ""

  const emptyValue = t("drilldown.myBrandDrawer.emptyValue", "—")
  const comingSoonLabel = t("drilldown.myBrandDrawer.comingSoon", "Coming soon")
  const noneLabel = t("drilldown.myBrandDrawer.none", "None")

  const parsed = useMemo(() => {
    return drawerData.section?.rows.length ? parseMyBrandDrawerRows(drawerData.section.rows) : null
  }, [drawerData.section?.rows])

  const narrative = useMemo(() => mapMyBrandDrawerNarrative(parsed), [parsed])

  const brandName = tenantName

  const model = useMemo(() => {
    if (!creative) return null
    return mapMyBrandDrawerModel(
      creative,
      drawerData.section,
      parsed,
      northStar,
      emptyValue,
      comingSoonLabel,
      noneLabel,
      brandName || emptyValue,
      tenantLogoUrl,
    )
  }, [
    creative,
    drawerData.section,
    parsed,
    northStar,
    emptyValue,
    comingSoonLabel,
    noneLabel,
    brandName,
    tenantLogoUrl,
  ])

  const handleOpenExample = useCallback(
    (row: TopicExampleRow) => {
      if (!creative || !onSelectCreative) return
      const next = exampleToCardModel(row)
      exampleNamesRef.current.set(next.hash, exampleName(row))
      setWhyBack({
        to: creative,
        forHash: next.hash,
        label: exampleNamesRef.current.get(creative.hash) ?? `creative ${creative.hash.slice(0, 8)}`,
      })
      onSelectCreative(next)
      tabScrollRef.current?.scrollTo({ top: 0 })
    },
    [creative, onSelectCreative],
  )

  const handleWhyBack = useCallback(() => {
    if (!whyBack || !onSelectCreative) return
    setWhyBack(null)
    onSelectCreative(whyBack.to)
    tabScrollRef.current?.scrollTo({ top: 0 })
  }, [whyBack, onSelectCreative])

  const whyBackLabel = whyBack && creative?.hash === whyBack.forHash ? whyBack.label : null

  const handleSequenceSeek = useCallback((seconds: number) => {
    mediaPreviewRef.current?.seekTo(seconds)
  }, [])

  const showAdIdsTab = drilldownFilters.segment1 === "Hash" && Boolean(creative?.hash)

  // An example stub (creative not in the grid) has no parent group: read its Ad Ids over the
  // whole creative, not the page's Group By split, or its All/All grouping matches no row.
  const isStub = creative ? isWhyExampleStub(creative) : false
  const adIdsFilters = useMemo(
    () => (isStub ? { ...drilldownFilters, segment2: "All", segment3: "All" } : drilldownFilters),
    [isStub, drilldownFilters],
  )

  useEffect(() => {
    if (!showAdIdsTab && activeTab === "adIds") {
      setActiveTab("info")
    }
  }, [showAdIdsTab, activeTab])

  const handleTabSelect = useCallback((tab: DrawerTab) => {
    setActiveTab(tab)
  }, [])

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) {
        setActiveTab("info")
        setWhyBack(null)
      }
      onOpenChange(next)
    },
    [onOpenChange],
  )

  const tabs: { id: DrawerTab; label: string }[] = useMemo(() => {
    const items: { id: DrawerTab; label: string }[] = [
      { id: "info", label: t("drilldown.myBrandDrawer.infoTab", "Info") },
    ]
    if (showAdIdsTab) {
      items.push({ id: "adIds", label: t("drilldown.myBrandDrawer.adIdsTab", "Ad Ids") })
    }
    items.push(
      { id: "performance", label: t("drilldown.myBrandDrawer.performanceTab", "Performance") },
      { id: "why", label: t("drilldown.myBrandDrawer.whyTab", "Why it works") },
      { id: "fatigue", label: t("drilldown.myBrandDrawer.fatigueTab", "Fatigue") },
    )
    return items
  }, [t, showAdIdsTab])

  return (
    <Sheet open={open} onOpenChange={handleOpenChange} modal={false}>
      <SheetContent
        side="right"
        closePosition="left"
        closeClassName={DRILLDOWN_DRAWER_CLOSE_CLASS}
        className="flex w-full flex-col overflow-hidden bg-card p-0 sm:max-w-[1080px]"
        onPointerDownOutside={(e) => {
          if (shouldKeepDrilldownDrawerOpenOnOutsideInteraction(e.target)) e.preventDefault()
        }}
        onInteractOutside={(e) => {
          if (shouldKeepDrilldownDrawerOpenOnOutsideInteraction(e.target)) e.preventDefault()
        }}
      >
        {model && creative ? (
          <div className="flex min-h-0 flex-1 flex-col sm:flex-row">
            <div className="flex min-h-0 w-full shrink-0 flex-col border-border bg-muted/40 sm:w-[44%] sm:border-r">
              <div className="border-b border-border px-6 py-4 sm:hidden">
                <DrawerTabList tabs={tabs} activeTab={activeTab} onSelect={handleTabSelect} />
              </div>
              <MediaPreviewPanel
                ref={mediaPreviewRef}
                key={model.id}
                contentKey={model.id}
                src={model.assetUrl || undefined}
                thumbnailSrc={model.thumbnailUrl}
                type={model.isVideo ? "video" : "image"}
                alt={model.brandName}
                renderFooterActions={({ activeSrc }) => (
                  <DrilldownCreativeLinkMenu
                    assetUrl={activeSrc ?? ""}
                    adId={creative.adId ?? null}
                    creativeId={creative.hash}
                    adLibUrl={model.adLibUrl}
                  />
                )}
                className="min-h-[320px] flex-1"
              />
            </div>

            <div ref={tabScrollRef} className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto">
              <div className="hidden border-b border-border px-6 py-4 sm:block">
                <DrawerTabList tabs={tabs} activeTab={activeTab} onSelect={handleTabSelect} />
              </div>
              {activeTab === "info" ? (
                <DrilldownMyBrandCreativeInfoTab
                  model={model}
                  narrative={narrative}
                  isVideo={model.isVideo}
                  isLoading={drawerData.isLoading}
                  isError={drawerData.isError}
                  onSequenceSeek={handleSequenceSeek}
                />
              ) : activeTab === "adIds" && showAdIdsTab ? (
                <DrilldownMyBrandCreativeAdIdsTab
                  hash={creative.hash}
                  grouping={creative.grouping}
                  myBrandFilters={myBrandFilters}
                  drilldownFilters={adIdsFilters}
                  currencySymbol={currencySymbol}
                />
              ) : activeTab === "performance" ? (
                <DrilldownMyBrandCreativePerformanceTab
                  performanceRow={performanceRow}
                  isLoading={performanceData.isLoading}
                  isError={performanceData.isError}
                />
              ) : activeTab === "why" ? (
                <DrilldownMyBrandCreativeWhyTab
                  section={whyData.section}
                  isLoading={whyData.isLoading}
                  isError={whyData.isError}
                  isUnavailable={whyData.isUnavailable}
                  northStar={northStar}
                  currencySymbol={currencySymbol}
                  currentHash={creative.hash}
                  tenantId={activeTenantId}
                  myBrandFilters={myBrandFilters}
                  drilldownFilters={drilldownFilters}
                  onOpenExample={onSelectCreative ? handleOpenExample : undefined}
                  backLabel={whyBackLabel}
                  onBack={handleWhyBack}
                />
              ) : activeTab === "fatigue" ? (
                <DrilldownMyBrandCreativeFatigueTab
                  seriesRows={fatigueData.section?.rows ?? null}
                  isLoading={fatigueData.isLoading}
                  isError={fatigueData.isError}
                  spanDays={fatigueSpanDays}
                  selectedMetrics={fatigueSelectedMetrics}
                  onSelectedMetricsChange={setFatigueSelectedMetrics}
                  granularity={fatigueGranularity}
                  onGranularityChange={setFatigueGranularity}
                  showPreviousPeriod={showPreviousPeriod}
                  onShowPreviousPeriodChange={setShowPreviousPeriod}
                />
              ) : null}
            </div>
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  )
}

function DrawerTabList({
  tabs,
  activeTab,
  onSelect,
}: {
  tabs: { id: DrawerTab; label: string }[]
  activeTab: DrawerTab
  onSelect: (tab: DrawerTab) => void
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          onClick={() => onSelect(tab.id)}
          className={
            tab.id === activeTab
              ? "inline-flex rounded-lg bg-primary/10 px-3 py-1.5 text-[13px] font-medium text-primary"
              : "inline-flex rounded-lg px-3 py-1.5 text-[13px] font-medium text-muted-foreground hover:bg-muted/60"
          }
        >
          {tab.label}
        </button>
      ))}
    </div>
  )
}
